import { useNavigate, useParams } from 'react-router-dom';
import { show as IntercomShow } from '@intercom/messenger-js-sdk';
import DashboardNavigationBar from '../../../../../components/dashboard/navigation-bar/dashboard-navigation-bar';
import DashboardLayout from '@/components/dashboard/dashboard-layout/dashboard-layout';
import DashboardGrid from '@/components/dashboard/dashboard-grid/dashboard-grid';
import SubNavBar from '@/components/subnav-bar/subnav-bar';
import Resources from '@/components/resources/resources';
import { useGetBiomarkersV1BiomarkersGet } from '../../../../../api/generated-catch-backend/biomarkers/biomarkers';
import { ROUTE_LABS } from '../../../../../constants/routes';
import { BiomarkerBadge } from '../biomarker-dashboard/biomarker-badge';
import { PortableText } from '@/app/components/questionnaireV2/components/portable-text/portable-text';
import { BiomarkerReferenceRange } from './biomarker-reference-range';
import { formatBiomarkerNotes } from '../../utils/format-biomarker-notes';
import '@/app/components/dashboard/dashboard-reset.css';

export function Page() {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();

  // Fetch all biomarkers
  const { data: biomarkersData, isLoading } = useGetBiomarkersV1BiomarkersGet();

  // Find the specific biomarker by slug
  const biomarker = biomarkersData?.categories
    .flatMap((category) => category.biomarkers)
    .find((b) => b.metadata.slug === slug);

  if (isLoading) {
    return (
      <>
        <DashboardNavigationBar activeItem="labs" variant="light" />
        <DashboardLayout>
          <DashboardGrid>
            <div className="flex items-center justify-center p-8">
              <div className="text-catch-dark-blue-90">Loading...</div>
            </div>
            <Resources as="aside" width="full" />
          </DashboardGrid>
        </DashboardLayout>
      </>
    );
  }

  if (!biomarker) {
    return (
      <>
        <DashboardNavigationBar activeItem="labs" variant="light" />
        <DashboardLayout>
          <DashboardGrid>
            <div className="flex flex-col items-center justify-center p-8">
              <div className="mb-4 text-2xl font-bold text-catch-dark-blue-90">Biomarker not found</div>
              <button
                onClick={() => navigate(ROUTE_LABS)}
                className="text-blue-600 underline hover:text-blue-800"
                type="button"
              >
                Back to Labs
              </button>
            </div>
            <Resources as="aside" width="full" />
          </DashboardGrid>
        </DashboardLayout>
      </>
    );
  }

  const hasResult = biomarker.resultValue !== null && biomarker.resultValue !== undefined;

  return (
    <>
      <DashboardNavigationBar activeItem="labs" variant="light" />

      <DashboardLayout>
        <DashboardGrid>
          <div className="dashboard-reset flex flex-col gap-6">
            {/* Back Navigation */}
            <SubNavBar onClick={() => navigate(ROUTE_LABS)}>Back</SubNavBar>

            {/* Header */}
            <div className="rounded-lg bg-white">
              <div className="flex items-start gap-4">
                <h1 className="text-3xl font-bold leading-snug text-catch-dark-blue-90">
                  {biomarker.metadata.displayName}
                </h1>
                <BiomarkerBadge classification={biomarker.classification} />
              </div>
              {hasResult && biomarker.resultValue && (() => {
                const { resultType, notes, result, unit } = biomarker.resultValue;

                if (resultType === 'comment') {
                  const displayValue = formatBiomarkerNotes(notes);
                  // Don't render anything if notes is empty
                  return displayValue ? (
                    <div className="mt-2 text-base font-semibold leading-snug text-catch-dark-blue-90">
                      {displayValue}
                    </div>
                  ) : null;
                }

                // For numeric/range types, display result and unit as before
                return (
                  <div className="mt-2 text-base font-semibold leading-snug text-catch-dark-blue-90">
                    {result} {unit}
                  </div>
                );
              })()}
            </div>

            {/* Your Results Card */}
            {hasResult && biomarker.resultValue ? (
              <BiomarkerReferenceRange resultValue={biomarker.resultValue} tierType={biomarker.metadata.tierType} />
            ) : (
              <div className="rounded-lg bg-white">
                <div className="flex flex-col gap-2">
                  <p className="text-gray-600">No results available yet for this biomarker.</p>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      IntercomShow();
                    }}
                    className="self-start text-sm font-bold text-catch-dark-blue-90 underline"
                    type="button"
                  >
                    Contact Customer Support
                  </button>
                </div>
              </div>
            )}

            {/* Content Sections */}
            {[
              { title: 'Your Summary', content: biomarker.longContent || biomarker.summaryContent },
              { title: 'Why It Matters', content: biomarker.whyItMatters },
              { title: 'What Makes Levels Fluctuate', content: biomarker.fluctuationNotes },
              { title: 'Supporting Healthy Levels', content: biomarker.stayingHealthy }
            ].map(
              (section, index) =>
                section.content &&
                section.content.length > 0 && (
                  <div
                    key={section.title}
                    className={`bg-white ${index > 0 ? 'border-t border-catch-cream pt-6' : ''} pb-6`}
                  >
                    <h2 className="mb-4 text-xl font-bold text-catch-dark-blue-90">{section.title}</h2>
                    <div className="text-base font-normal leading-[130%] text-catch-dark-blue-90">
                      <PortableText content={section.content} />
                    </div>
                  </div>
                ),
            )}
          </div>

          <Resources as="aside" width="full" />
        </DashboardGrid>
      </DashboardLayout>
    </>
  );
}
